import { useCallback, useRef } from 'react';
import { focusElement } from 'platform/utilities/ui';

/**
 * Tracks the composer's input element and programmatically focuses it.
 * `handleComposerInputReady` is wired to ChatComposer's onComposerInputReady
 * so the ref survives the composer's token-ready remount.
 */
export default function useComposerFocus() {
  const composerInputRef = useRef(null);

  // va-text-input doesn't delegate focus, so focus must land on the input
  // inside its shadow DOM rather than the host element.
  const focusComposerInput = useCallback(() => {
    if (composerInputRef.current) {
      focusElement('input', {}, composerInputRef.current);
    }
  }, []);

  const handleComposerInputReady = useCallback(
    element => {
      const previousInput = composerInputRef.current;
      composerInputRef.current = element;

      // Focus the composer on mount and across its token-ready remount,
      // unless the user has already moved focus elsewhere.
      const { activeElement } = document;
      if (
        element &&
        (activeElement === document.body || activeElement === previousInput)
      ) {
        focusComposerInput();
      }
    },
    [focusComposerInput],
  );

  return { focusComposerInput, handleComposerInputReady };
}
